import { useEffect, useState } from "react";
import { useGo, useList } from "@refinedev/core";
import {
  Boxes,
  Bot,
  FileCheck2,
  FolderKanban,
  GaugeCircle,
  ListChecks,
  Plus,
  Sparkles,
  Target,
} from "@/components/ui/icon";

import type { Project, AgentRun } from "@aos/shared";
import { OPEN_COMMAND_MENU_EVENT } from "@/components/layout/Header";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/command-menu/command";

/** Resources reachable from the palette ("Search resources"). */
const RESOURCE_LINKS: { resource: string; label: string }[] = [
  { resource: "projects", label: "Projects" },
  { resource: "tasks", label: "Tasks" },
  { resource: "objectives", label: "Objectives" },
  { resource: "key-results", label: "Key Results" },
  { resource: "metrics", label: "Metrics" },
  { resource: "measurements", label: "Measurements" },
  { resource: "skills", label: "Skills" },
  { resource: "processes", label: "Processes" },
  { resource: "phases", label: "Phases" },
  { resource: "rules", label: "Rules" },
  { resource: "evidence", label: "Evidence" },
  { resource: "decisions", label: "Decisions" },
  { resource: "documents", label: "Documents" },
  // Context-centric data-model entities (v0.2). Made reachable from ⌘K so no
  // resource is URL-only (frontend-entity-coverage §6.1).
  { resource: "contexts", label: "Contexts" },
  { resource: "context-bindings", label: "Context Bindings" },
  { resource: "signals", label: "Signals" },
  { resource: "packages", label: "Packages" },
  { resource: "package-links", label: "Package Links" },
  { resource: "claims", label: "Claims" },
  { resource: "artifact-positions", label: "Artifact Positions" },
  { resource: "timers", label: "Timers" },
  // Org / identity (Milestone A) — also in the Admin sidebar group.
  { resource: "companies", label: "Companies" },
  { resource: "users", label: "Users" },
  { resource: "roles", label: "Roles" },
  { resource: "agent-runs", label: "Agent Runs" },
  { resource: "events", label: "Events" },
  { resource: "rule-runs", label: "Rule Runs" },
  // Runtime/observability [PROPOSED] modules — reachable from ⌘K so no resource
  // is URL-only (frontend-entity-coverage §6.1).
  { resource: "event-sources", label: "Event Sources" },
  { resource: "effect-runs", label: "Effect Runs" },
  { resource: "transition-proposals", label: "Transition Proposals" },
  { resource: "contour-instances", label: "Contour Instances" },
];

/**
 * CommandMenu (D6) — cmdk command palette bound to Cmd/Ctrl+K (frontend.spec §19).
 * Capabilities: search resources, create task/skill/decision, open a project or
 * an agent run. Mount once near the app root (see App.tsx wiring in report).
 */
export function CommandMenu() {
  const go = useGo();
  const [open, setOpen] = useState(false);

  // Cmd+K / Ctrl+K toggles the palette globally; the Header faux-search
  // trigger opens it via OPEN_COMMAND_MENU_EVENT (§4.2 / §4.4).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    const onOpen = () => setOpen(true);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener(OPEN_COMMAND_MENU_EVENT, onOpen);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener(OPEN_COMMAND_MENU_EVENT, onOpen);
    };
  }, []);

  // Recent records for quick-open. cmdk filters these client-side by label.
  const { data: projectData } = useList<Project>({
    resource: "projects",
    pagination: { pageSize: 25 },
    sorters: [{ field: "created_at", order: "desc" }],
    queryOptions: { enabled: open },
  });
  const { data: agentRunData } = useList<AgentRun>({
    resource: "agent-runs",
    pagination: { pageSize: 25 },
    sorters: [{ field: "created_at", order: "desc" }],
    queryOptions: { enabled: open },
  });
  const projects = projectData?.data ?? [];
  const agentRuns = agentRunData?.data ?? [];

  const run = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="overflow-hidden p-0 shadow-lg [&>button]:hidden">
        <DialogTitle className="sr-only">Command menu</DialogTitle>
        <Command>
          <CommandInput placeholder="Search resources or run a command…" />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>

            <CommandGroup heading="Create">
              <CommandItem
                onSelect={() => run(() => go({ to: { resource: "tasks", action: "create" } }))}
              >
                <Plus /> Create task
              </CommandItem>
              <CommandItem
                onSelect={() => run(() => go({ to: { resource: "skills", action: "create" } }))}
              >
                <Sparkles /> Create skill
              </CommandItem>
              <CommandItem
                onSelect={() => run(() => go({ to: { resource: "decisions", action: "create" } }))}
              >
                <FileCheck2 /> Create decision
              </CommandItem>
            </CommandGroup>

            <CommandSeparator />

            <CommandGroup heading="Go to">
              <CommandItem onSelect={() => run(() => go({ to: "/dashboard" }))}>
                <GaugeCircle /> Dashboard
              </CommandItem>
              <CommandItem onSelect={() => run(() => go({ to: "/okr-dashboard" }))}>
                <Target /> OKR Dashboard
              </CommandItem>
              <CommandItem onSelect={() => run(() => go({ to: "/operations" }))}>
                <ListChecks /> Operations
              </CommandItem>
            </CommandGroup>

            <CommandSeparator />

            <CommandGroup heading="Resources">
              {RESOURCE_LINKS.map((r) => (
                <CommandItem
                  key={r.resource}
                  value={`resource ${r.label}`}
                  onSelect={() => run(() => go({ to: { resource: r.resource, action: "list" } }))}
                >
                  <Boxes /> {r.label}
                </CommandItem>
              ))}
            </CommandGroup>

            {projects.length > 0 ? (
              <>
                <CommandSeparator />
                <CommandGroup heading="Open project">
                  {projects.map((p) => (
                    <CommandItem
                      key={p.id}
                      value={`project ${p.name} ${p.id}`}
                      onSelect={() =>
                        run(() => go({ to: { resource: "projects", action: "show", id: p.id } }))
                      }
                    >
                      <FolderKanban /> {p.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            ) : null}

            {agentRuns.length > 0 ? (
              <>
                <CommandSeparator />
                <CommandGroup heading="Open agent run">
                  {agentRuns.map((ar) => (
                    <CommandItem
                      key={ar.id}
                      value={`agent run ${ar.id} ${ar.status}`}
                      onSelect={() =>
                        run(() => go({ to: { resource: "agent-runs", action: "show", id: ar.id } }))
                      }
                    >
                      <Bot /> {ar.id}
                      <span className="ml-auto text-xs text-muted-foreground">{ar.status}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            ) : null}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

export default CommandMenu;
